import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Minus,
  CheckCircle, XCircle, Mail, Save,
  Calendar, Loader2, RefreshCw, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import MobileNav from '../components/MobileNav';

const API = process.env.REACT_APP_BACKEND_URL;

const BUREAUS = [
  { key: 'equifax', name: 'EQUIFAX', color: 'red' },
  { key: 'experian', name: 'EXPERIAN', color: 'blue' },
  { key: 'transunion', name: 'TRANSUNION', color: 'green' }
];

export default function UpdateClientProgress() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  
  // State
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // All accounts from database
  const [allAccounts, setAllAccounts] = useState([]);
  
  // Account status per bureau: { accountId_bureau: 'disputed' | 'deleted' | 'stayed' }
  const [accountBureauStatus, setAccountBureauStatus] = useState({});
  
  // Score update form
  const [scoreForm, setScoreForm] = useState({
    equifax: '',
    experian: '',
    transunion: '',
    notes: ''
  });
  
  // Previous scores for comparison
  const [previousScores, setPreviousScores] = useState({
    equifax: null,
    experian: null,
    transunion: null
  });
  
  // SIMULATED SCORES - Updated when accounts are marked deleted
  const [simulatedScores, setSimulatedScores] = useState({
    equifax: null,
    experian: null,
    transunion: null
  });
  
  // Update history log
  const [updateHistory, setUpdateHistory] = useState([]);
  
  // Deleted accounts from database
  const [deletedAccountsLog, setDeletedAccountsLog] = useState([]);
  
  // Load client data and accounts
  useEffect(() => {
    const loadData = async () => {
      if (!clientId) return;
      
      try {
        // Load client
        const clientResponse = await axios.get(`${API}/api/clients/${clientId}`);
        setClient(clientResponse.data);
        
        // Set previous scores
        setPreviousScores({
          equifax: clientResponse.data.score_equifax,
          experian: clientResponse.data.score_experian,
          transunion: clientResponse.data.score_transunion
        });
        
        // Pre-fill current scores
        setScoreForm({
          equifax: clientResponse.data.score_equifax || '',
          experian: clientResponse.data.score_experian || '',
          transunion: clientResponse.data.score_transunion || '',
          notes: ''
        });
        
        // Load all accounts
        const accountsResponse = await axios.get(`${API}/api/accounts?client_id=${clientId}`);
        setAllAccounts(accountsResponse.data || []);
        
        // Load existing deleted accounts to get status
        try {
          const deletedResponse = await axios.get(`${API}/api/clients/${clientId}/deleted-accounts`);
          setDeletedAccountsLog(deletedResponse.data || []);
          
          // Build status map from deleted accounts
          const statusMap = {};
          (deletedResponse.data || []).forEach(del => {
            // Match by creditor name and bureau
            const bureaus = del.bureau?.toLowerCase().split(',').map(b => b.trim()) || ['all'];
            bureaus.forEach(bureau => {
              if (bureau === 'all') {
                ['equifax', 'experian', 'transunion'].forEach(b => {
                  statusMap[`${del.creditor_name}_${b}`] = del.verified ? 'deleted' : 'deleted';
                });
              } else {
                statusMap[`${del.creditor_name}_${bureau}`] = 'deleted';
              }
            });
          });
          setAccountBureauStatus(statusMap);
        } catch (e) {
          console.log('No deleted accounts yet');
        }
        
        // Load update history
        try {
          const historyResponse = await axios.get(`${API}/api/clients/${clientId}/score-history`);
          setUpdateHistory(historyResponse.data || []);
        } catch (e) {
          console.log('No score history yet');
        }
        
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load client data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [clientId]);
  
  // Get accounts for a specific bureau
  const getAccountsForBureau = (bureauKey) => {
    return allAccounts.filter(acc => {
      const bureaus = acc.bureaus_present || acc.bureaus || [];
      return bureaus.some(b => b.toLowerCase() === bureauKey.toLowerCase());
    });
  };
  
  // Get status for account+bureau combination
  const getAccountBureauStatus = (account, bureauKey) => {
    const key = `${account.creditor_name}_${bureauKey}`;
    return accountBureauStatus[key] || 'disputed';
  };
  
  // Calculate score change
  const getScoreChange = (current, previous) => {
    if (!current || !previous) return null;
    return parseInt(current) - parseInt(previous);
  };
  
  // Render score change indicator
  const renderScoreChange = (change) => {
    if (change === null) return null;
    if (change > 0) {
      return (
        <span className="flex items-center text-green-400 text-sm font-medium">
          <TrendingUp className="w-4 h-4 mr-1" />
          +{change} pts
        </span>
      );
    } else if (change < 0) {
      return (
        <span className="flex items-center text-red-400 text-sm font-medium">
          <TrendingDown className="w-4 h-4 mr-1" />
          {change} pts
        </span>
      );
    }
    return (
      <span className="flex items-center text-muted-foreground text-sm">
        <Minus className="w-4 h-4 mr-1" />
        No change
      </span>
    );
  };
  
  // Save scores and update client profile
  const handleSaveScores = async () => {
    const eq = scoreForm.equifax ? parseInt(scoreForm.equifax) : null;
    const ex = scoreForm.experian ? parseInt(scoreForm.experian) : null;
    const tu = scoreForm.transunion ? parseInt(scoreForm.transunion) : null;
    
    if (!eq && !ex && !tu) {
      toast.error('Please enter at least one score');
      return;
    }
    
    setSaving(true);
    try {
      // Update client scores
      await axios.put(`${API}/api/clients/${clientId}`, {
        name: client?.name,
        email: client?.email,
        phone: client?.phone || "",
        address: client?.address || "",
        score_equifax: eq || client?.score_equifax,
        score_experian: ex || client?.score_experian,
        score_transunion: tu || client?.score_transunion
      });
      
      // Save to score history
      await axios.post(`${API}/api/clients/${clientId}/score-snapshot`, {
        equifax: eq || client?.score_equifax,
        experian: ex || client?.score_experian,
        transunion: tu || client?.score_transunion,
        notes: scoreForm.notes || 'Progress update',
        source: 'progress_update'
      });
      
      // Update previous scores
      setPreviousScores({
        equifax: eq || previousScores.equifax,
        experian: ex || previousScores.experian,
        transunion: tu || previousScores.transunion
      });
      
      // Refresh history
      const historyResponse = await axios.get(`${API}/api/clients/${clientId}/score-history`);
      setUpdateHistory(historyResponse.data || []);
      
      toast.success('Credit scores updated and saved to history!');
    } catch (error) {
      console.error('Error saving scores:', error);
      toast.error('Failed to save scores');
    } finally {
      setSaving(false);
    }
  };
  
  // Mark account as deleted for specific bureau
  const handleMarkDeleted = async (account, bureauKey) => {
    const key = `${account.creditor_name}_${bureauKey}`;
    
    try {
      // Log to deleted accounts collection
      await axios.post(`${API}/api/clients/${clientId}/deleted-accounts`, {
        creditor_name: account.creditor_name,
        account_number: account.account_number,
        bureau: bureauKey,
        deleted_date: new Date().toISOString().split('T')[0],
        notes: `Marked as DELETED from ${bureauKey.toUpperCase()}`
      });
      
      // Update local state
      setAccountBureauStatus(prev => ({
        ...prev,
        [key]: 'deleted'
      }));
      
      toast.success(`${account.creditor_name} marked as DELETED. Score simulating...`);
    } catch (error) {
      // If endpoint doesn't exist, just update locally
      setAccountBureauStatus(prev => ({
        ...prev,
        [key]: 'deleted'
      }));
      toast.success(`${account.creditor_name} marked as DELETED. Score simulating...`);
    }
  };
  
  // Mark account as stayed for specific bureau
  const handleMarkStayed = async (account, bureauKey) => {
    const key = `${account.creditor_name}_${bureauKey}`;
    
    try {
      // Log to progress (could create a stayed_accounts collection or use progress log)
      await axios.post(`${API}/api/clients/${clientId}/account-status`, {
        account_id: account.id,
        creditor_name: account.creditor_name,
        bureau: bureauKey,
        status: 'stayed',
        date: new Date().toISOString().split('T')[0],
        notes: `Account remained on ${bureauKey.toUpperCase()} report`
      });
      
      // Update local state
      setAccountBureauStatus(prev => ({
        ...prev,
        [key]: 'stayed'
      }));
      
      toast.info(`${account.creditor_name} marked as STAYED on ${bureauKey.toUpperCase()}`);
    } catch (error) {
      // If endpoint doesn't exist, just update locally
      setAccountBureauStatus(prev => ({
        ...prev,
        [key]: 'stayed'
      }));
      toast.info(`${account.creditor_name} marked as STAYED on ${bureauKey.toUpperCase()}`);
    }
  };
  
  // Reset account status for specific bureau
  const handleResetStatus = (account, bureauKey) => {
    const key = `${account.creditor_name}_${bureauKey}`;
    setAccountBureauStatus(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });
  };
  
  // Send progress email to client
  const handleSendProgressEmail = async () => {
    if (!client?.email) {
      toast.error('Client email not found');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/api/email/send-progress-update`, {
        client_id: clientId
      });
      toast.success(`Progress update email sent to ${client.email}!`);
    } catch (error) {
      toast.error('Failed to send email: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Get bureau color classes
  const getBureauColors = (bureauKey) => {
    switch (bureauKey) {
      case 'equifax':
        return { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500' };
      case 'experian':
        return { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500' };
      case 'transunion':
        return { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', badge: 'bg-green-500' };
      default:
        return { bg: 'bg-secondary/30', border: 'border-white/10', text: 'text-foreground', badge: 'bg-primary' };
    }
  };
  
  // Count deleted/stayed per bureau
  const getCountsForBureau = (bureauKey) => {
    const accounts = getAccountsForBureau(bureauKey);
    let deleted = 0, stayed = 0, pending = 0;
    accounts.forEach(acc => {
      const status = getAccountBureauStatus(acc, bureauKey);
      if (status === 'deleted') deleted++;
      else if (status === 'stayed') stayed++;
      else pending++;
    });
    return { deleted, stayed, pending, total: accounts.length };
  };
  
  // SCORE SIMULATION - Calculate estimated score based on deleted accounts
  // Recalculate simulated scores when account statuses change
  useEffect(() => {
    const runSimulation = async () => {
      for (const bureau of BUREAUS) {
        const bureauKey = bureau.key;
        const currentScore = previousScores[bureauKey];
        
        // Skip if no current score set
        if (!currentScore) {
          console.log(`[SCORE_SIM] Skipping ${bureauKey} - no previous score`);
          continue;
        }
        
        // Count deleted accounts for this bureau
        const accounts = allAccounts.filter(acc => {
          const bureaus = acc.bureaus_present || acc.bureaus || [];
          return bureaus.some(b => b.toLowerCase() === bureauKey.toLowerCase());
        });
        
        let deletedCount = 0;
        accounts.forEach(acc => {
          const key = `${acc.creditor_name}_${bureauKey}`;
          if (accountBureauStatus[key] === 'deleted') {
            deletedCount++;
          }
        });
        
        console.log(`[SCORE_SIM] ${bureauKey}: current=${currentScore}, deleted=${deletedCount}, total=${accounts.length}`);
        
        if (deletedCount === 0) {
          // Reset simulated score if no accounts deleted
          setSimulatedScores(prev => ({ ...prev, [bureauKey]: null }));
          continue;
        }
        
        try {
          // Call the quick score estimate API
          const response = await axios.post(`${API}/api/clients/${clientId}/quick-score-estimate`, {
            current_score: parseInt(currentScore),
            total_negative_accounts: accounts.length,
            accounts_removed: deletedCount
          });
          
          console.log(`[SCORE_SIM] ${bureauKey} API response:`, response.data);
          
          if (response.data.success) {
            const projected = response.data.estimated_score.projected;
            setSimulatedScores(prev => ({ ...prev, [bureauKey]: projected }));
            
            // Auto-fill the score form with simulated score
            setScoreForm(prev => ({ ...prev, [bureauKey]: projected.toString() }));
            
            toast.success(`${bureauKey.toUpperCase()}: Score projected to increase to ${projected} (+${projected - currentScore} points)`);
          }
        } catch (error) {
          console.error(`Score simulation error for ${bureauKey}:`, error);
          // Fallback: Simple calculation
          const impactPerAccount = 25;
          const estimated = Math.min(850, parseInt(currentScore) + (deletedCount * impactPerAccount));
          setSimulatedScores(prev => ({ ...prev, [bureauKey]: estimated }));
          setScoreForm(prev => ({ ...prev, [bureauKey]: estimated.toString() }));
        }
      }
    };
    
    // Only run if we have accounts loaded
    if (allAccounts.length > 0 && Object.keys(accountBureauStatus).length > 0) {
      runSimulation();
    }
  }, [accountBureauStatus, previousScores, allAccounts, clientId]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background py-8">
      <MobileNav />
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => navigate(`/clients/${clientId}`)}
              className="flex items-center gap-2 border-white/10 hover:bg-secondary"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Profile
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Update Client Progress</h1>
              <p className="text-muted-foreground">{client?.name}</p>
            </div>
          </div>
          
          <Button 
            onClick={handleSendProgressEmail}
            disabled={sendingEmail}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="send-progress-email-btn"
          >
            {sendingEmail ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Email Progress to Client
              </>
            )}
          </Button>
        </div>
        
        {/* Score Update Section */}
        <Card className="mb-6 border-white/5 shadow-xl shadow-black/20">
          <CardHeader className="bg-blue-500/10 border-b border-white/5">
            <CardTitle className="flex items-center gap-2 text-blue-400">
              <TrendingUp className="w-5 h-5" />
              Update Credit Scores
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Equifax */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-red-400">Equifax</Label>
                <Input
                  type="number"
                  min="300"
                  max="850"
                  placeholder="Enter score"
                  value={scoreForm.equifax}
                  onChange={(e) => setScoreForm({ ...scoreForm, equifax: e.target.value })}
                  className="text-2xl font-bold text-center h-16 bg-secondary/50 border-white/10"
                  data-testid="equifax-score-input"
                />
                <div className="text-center">
                  {previousScores.equifax && (
                    <p className="text-xs text-muted-foreground">Starting: {previousScores.equifax}</p>
                  )}
                  {simulatedScores.equifax && (
                    <div className="mt-1 p-2 bg-green-500/10 rounded-lg border border-green-500/30">
                      <p className="text-xs text-green-400 flex items-center justify-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Simulated: <span className="font-bold">{simulatedScores.equifax}</span>
                        <span className="text-green-300">(+{simulatedScores.equifax - previousScores.equifax})</span>
                      </p>
                    </div>
                  )}
                  {scoreForm.equifax && previousScores.equifax && !simulatedScores.equifax && (
                    <div className="mt-1">
                      {renderScoreChange(getScoreChange(scoreForm.equifax, previousScores.equifax))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Experian */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-blue-400">Experian</Label>
                <Input
                  type="number"
                  min="300"
                  max="850"
                  placeholder="Enter score"
                  value={scoreForm.experian}
                  onChange={(e) => setScoreForm({ ...scoreForm, experian: e.target.value })}
                  className="text-2xl font-bold text-center h-16 bg-secondary/50 border-white/10"
                  data-testid="experian-score-input"
                />
                <div className="text-center">
                  {previousScores.experian && (
                    <p className="text-xs text-muted-foreground">Starting: {previousScores.experian}</p>
                  )}
                  {simulatedScores.experian && (
                    <div className="mt-1 p-2 bg-green-500/10 rounded-lg border border-green-500/30">
                      <p className="text-xs text-green-400 flex items-center justify-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Simulated: <span className="font-bold">{simulatedScores.experian}</span>
                        <span className="text-green-300">(+{simulatedScores.experian - previousScores.experian})</span>
                      </p>
                    </div>
                  )}
                  {scoreForm.experian && previousScores.experian && !simulatedScores.experian && (
                    <div className="mt-1">
                      {renderScoreChange(getScoreChange(scoreForm.experian, previousScores.experian))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* TransUnion */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-green-400">TransUnion</Label>
                <Input
                  type="number"
                  min="300"
                  max="850"
                  placeholder="Enter score"
                  value={scoreForm.transunion}
                  onChange={(e) => setScoreForm({ ...scoreForm, transunion: e.target.value })}
                  className="text-2xl font-bold text-center h-16 bg-secondary/50 border-white/10"
                  data-testid="transunion-score-input"
                />
                <div className="text-center">
                  {previousScores.transunion && (
                    <p className="text-xs text-muted-foreground">Starting: {previousScores.transunion}</p>
                  )}
                  {simulatedScores.transunion && (
                    <div className="mt-1 p-2 bg-green-500/10 rounded-lg border border-green-500/30">
                      <p className="text-xs text-green-400 flex items-center justify-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Simulated: <span className="font-bold">{simulatedScores.transunion}</span>
                        <span className="text-green-300">(+{simulatedScores.transunion - previousScores.transunion})</span>
                      </p>
                    </div>
                  )}
                  {scoreForm.transunion && previousScores.transunion && !simulatedScores.transunion && (
                    <div className="mt-1">
                      {renderScoreChange(getScoreChange(scoreForm.transunion, previousScores.transunion))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <Label className="text-sm font-medium text-foreground">Notes (optional)</Label>
              <Input
                placeholder="e.g., Round 2 update, 30-day check"
                value={scoreForm.notes}
                onChange={(e) => setScoreForm({ ...scoreForm, notes: e.target.value })}
                className="bg-secondary/50 border-white/10"
                data-testid="notes-input"
              />
            </div>
            
            <Button 
              onClick={handleSaveScores} 
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="save-scores-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Scores & Update Profile
                </>
              )}
            </Button>
          </CardContent>
        </Card>
        
        {/* Accounts Per Bureau */}
        <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-amber-400" />
          Disputed Accounts by Bureau
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {BUREAUS.map(bureau => {
            const colors = getBureauColors(bureau.key);
            const accounts = getAccountsForBureau(bureau.key);
            const counts = getCountsForBureau(bureau.key);
            
            return (
              <Card key={bureau.key} className={`border-white/5 shadow-xl shadow-black/20`}>
                <CardHeader className={`${colors.bg} border-b ${colors.border} py-3`}>
                  <div className="flex items-center justify-between">
                    <CardTitle className={`text-lg font-bold ${colors.text}`}>
                      {bureau.name}
                    </CardTitle>
                    <Badge className={`${colors.badge} text-white`}>
                      {accounts.length} accounts
                    </Badge>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Badge className="bg-green-500/20 text-green-400 text-xs">{counts.deleted} Deleted</Badge>
                    <Badge className="bg-red-500/20 text-red-400 text-xs">{counts.stayed} Stayed</Badge>
                    <Badge className="bg-amber-500/20 text-amber-400 text-xs">{counts.pending} Pending</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-3 max-h-[400px] overflow-y-auto">
                  {accounts.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4 text-sm">
                      No accounts for this bureau
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {accounts.map((account, idx) => {
                        const status = getAccountBureauStatus(account, bureau.key);
                        
                        return (
                          <div 
                            key={`${account.id}-${bureau.key}-${idx}`}
                            className={`p-3 rounded-lg border ${
                              status === 'deleted' 
                                ? 'bg-green-500/10 border-green-500/30' 
                                : status === 'stayed'
                                ? 'bg-red-500/10 border-red-500/30'
                                : 'bg-secondary/30 border-white/10'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <p className={`font-semibold text-sm ${
                                status === 'deleted' ? 'line-through text-green-400' : 'text-foreground'
                              }`}>
                                {account.creditor_name}
                              </p>
                              {status === 'deleted' && (
                                <Badge className="bg-green-500 text-white text-xs">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  DELETED
                                </Badge>
                              )}
                              {status === 'stayed' && (
                                <Badge className="bg-red-500 text-white text-xs">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  STAYED
                                </Badge>
                              )}
                            </div>
                            
                            <p className="text-xs text-muted-foreground mb-2">
                              {account.account_number && `#${account.account_number} • `}
                              ${(account.balance || 0).toLocaleString()}
                            </p>
                            
                            {/* Action Buttons */}
                            {status === 'disputed' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 border-green-500/50 text-green-400 hover:bg-green-500/20 text-xs h-8"
                                  onClick={() => handleMarkDeleted(account, bureau.key)}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Deleted
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20 text-xs h-8"
                                  onClick={() => handleMarkStayed(account, bureau.key)}
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Stayed
                                </Button>
                              </div>
                            )}
                            
                            {/* Reset Button */}
                            {status !== 'disputed' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="w-full text-muted-foreground hover:text-foreground text-xs h-7"
                                onClick={() => handleResetStatus(account, bureau.key)}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Reset Status
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Update History */}
        <Card className="border-white/5 shadow-xl shadow-black/20">
          <CardHeader className="bg-secondary/30 border-b border-white/5">
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Calendar className="w-5 h-5 text-primary" />
              Update History Log
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {updateHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No update history yet.</p>
            ) : (
              <div className="space-y-2">
                {updateHistory.slice(0, 10).map((entry, idx) => {
                  const dateStr = entry.created_at || entry.extracted_at || entry.date;
                  const dateDisplay = dateStr ? new Date(dateStr).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  }) : 'Unknown';
                  
                  const hasIndividualScores = entry.equifax || entry.experian || entry.transunion;
                  
                  return (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg border border-white/5"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-sm text-muted-foreground min-w-[80px]">
                          {dateDisplay}
                        </div>
                        <div className="flex gap-3 text-sm">
                          {hasIndividualScores ? (
                            <>
                              <span className="text-red-400">EQ: {entry.equifax || '---'}</span>
                              <span className="text-blue-400">EX: {entry.experian || '---'}</span>
                              <span className="text-green-400">TU: {entry.transunion || '---'}</span>
                            </>
                          ) : entry.score ? (
                            <span className="text-blue-400">
                              {(entry.source_bureau || 'Score').toUpperCase()}: {entry.score}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">No scores</span>
                          )}
                        </div>
                      </div>
                      {entry.notes && (
                        <span className="text-xs text-muted-foreground">{entry.notes}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
